import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CircleAlert, Wallet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmt = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString([], { month: "short", day: "numeric" }) : "");

function ExceptionLine({ row, canResolve, onResolve }) {
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`payroll-exception-${row.id}`}>
      <div className="payroll-exception-head">
        <span><strong>{row.person_name}</strong> · {row.label} · {fmt(row.date)}{row.client_name ? ` · ${row.client_name}` : ""}</span>
        {canResolve && !open && <button type="button" className="provider-profile-link" data-testid={`payroll-resolve-open-${row.id}`} onClick={() => setOpen(true)}>Resolve…</button>}
      </div>
      {open && (
        <div className="verification-actions">
          <input data-testid={`payroll-resolve-note-${row.id}`} placeholder="Resolution note (required)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="button button-primary" data-testid={`payroll-resolve-submit-${row.id}`} disabled={note.trim().length < 3} onClick={() => onResolve(row, note.trim())}>Mark resolved</button>
          <button type="button" className="provider-profile-link" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
    </li>
  );
}

export const PayrollReadinessCard = ({ user }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get("/attendance/payroll-readiness")).data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function resolve(row, note) {
    try {
      await api.post(`/attendance/time-exceptions/${row.id}/resolve`, { note });
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  if (!data?.show) return null;
  const canResolve = data.resolve_roles.includes(user?.role);
  return (
    <section className="ops-card payroll-readiness" data-testid="payroll-readiness-card">
      <header className="payroll-readiness-head">
        <div><p className="eyebrow"><Wallet size={13} /> Payroll readiness</p><h3>Time exceptions before pay runs</h3></div>
      </header>
      {error && <p className="form-message error-message" data-testid="payroll-readiness-error">{error}</p>}
      {data.periods.map((period) => (
        <article className="payroll-period" key={period.kind + period.start} data-testid={`payroll-period-${period.kind}`}>
          <div className="payroll-period-head">
            <div><strong>{period.label}</strong><br /><small className="pairing-meta">Pays {fmt(period.pays_on)} · {period.resolved_count} resolved</small></div>
            <span className={`ops-pill ${period.ready ? "is-green" : "is-red"}`} data-testid={`payroll-status-${period.kind}`}>{period.ready ? <><CheckCircle2 size={13} /> Ready</> : <><CircleAlert size={13} /> {period.unresolved_count} unresolved</>}</span>
          </div>
          {period.unresolved.length > 0 && (
            <ul className="payroll-exception-list">
              {period.unresolved.map((row) => <ExceptionLine key={row.id} row={row} canResolve={canResolve} onResolve={resolve} />)}
            </ul>
          )}
          {period.ready && <p className="pairing-meta" data-testid={`payroll-ready-note-${period.kind}`}>Every time exception for this period is resolved in TAGForce with a note.</p>}
        </article>
      ))}
    </section>
  );
};
